import React, { useEffect, useState } from "react";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, FormHelperText, Stack } from "@mui/material";
import { every } from "lodash";
import { FormProvider, useForm } from "react-hook-form";
import { useSelector } from "react-redux";

import { Partner } from "@fartherfinance/frontend/api/Dashboard/requests/getClientDashboard";
import useGetAdvisor from "@fartherfinance/frontend/api/Entity/hooks/useGetAdvisor";
import usePatchAdvisor from "@fartherfinance/frontend/api/Entity/hooks/usePatchAdvisor";
import { ServiceTeam } from "@fartherfinance/frontend/api/Entity/Types";
import {
  partners,
  USAState,
  USAStates,
} from "@fartherfinance/frontend/api/Types";

import Drawer from "../Drawer/Drawer";
import ButtonPrimary from "../MUI/Button/Button";
import { emailValidationRegex } from "@src/multiCustodian/components/Advisor/ClientProfile/Forms/constants";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import FlexWrapper from "@src/sharedComponents/Forms/FlexWrapper";
import FormDropdownField from "@src/sharedComponents/Forms/FormDropdownField";
import FormTextareaField from "@src/sharedComponents/Forms/FormTextareaField";
import FormTextField from "@src/sharedComponents/Forms/FormTextField";
import FormFieldLabel from "@src/sharedComponents/Forms/Private/FormFieldLabel";
import Spacer from "@src/sharedComponents/Forms/Spacer";
import LogoLoadingStill from "@src/sharedComponents/LogoLoadingStill/LogoLoadingStill";
import FileDropUploadComponent from "@src/sharedComponents/SingleFileDropUpload/SingleFileDropUpload";
import Tooltip from "@src/sharedComponents/Tooltip/Tooltip";
import { State } from "@src/store";

import styles from "./EditProfileDrawer.module.css";

const URL_REGEX = /^https:\/\/[^\s\/$.?#].[^\s]*$/;

const BIO_MIN_LENGTH = 5;

const FirstName = "FirstName";
const LastName = "LastName";
const FartherEmail = "FartherEmail";
const Organization = "Organization";
const ServiceTeamField = "ServiceTeamField";
const CurrentCity = "CurrentCity";
const CurrentState = "CurrentState";
const Hometown = "Hometown";
const University = "University";
const Concentration = "Concentration";
const CalendarUrl = "CalendarUrl";
const LinkedinProfile = "LinkedinProfile";
const Bio = "Bio";

interface Form {
  [FirstName]: string | undefined;
  [LastName]: string | undefined;
  [FartherEmail]: string | undefined;
  [Organization]: { label: Partner } | undefined;
  [ServiceTeamField]: { label: ServiceTeam } | undefined;
  [CurrentCity]: string | undefined;
  [CurrentState]: { label: USAState } | undefined;
  [Hometown]: string | undefined;
  [University]: string | undefined;
  [Concentration]: string | undefined;
  [CalendarUrl]: string | undefined;
  [LinkedinProfile]: string | undefined;
  [Bio]: string | undefined;
}

type ValidForm = StripUndefined<Form>;

const formIsValid = (f: Form): f is ValidForm => {
  return every(f, (v) => v !== undefined);
};

interface Props {
  onClose: () => void;
}

const EditProfileDrawer: React.FC<Props> = ({ onClose }) => {
  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [imgError, setImgError] = useState<string | null>(null);

  // Get advisor data from Redux for IDs and status
  const { advisorId } = useSelector((state: State) => ({
    advisorId: state.main_Reducer.cockroach_advisor_id,
    hasCompletedAccountSelections:
      state.main_Reducer.has_completed_account_selections,
    dashboardStatus: state.main_Reducer.dashboard_status,
  }));

  // Get auth config
  const config = useAdvisorRequestAuth();

  // Use the new hook to get advisor data
  const { data: currentAdvisor, isLoading: isLoadingAdvisor } = useGetAdvisor(
    advisorId!,
    config
  );

  const statusNotification = useStatusNotification();

  // Initialize patch advisor hook
  const { mutateAsync: patchAdvisor, isLoading: isSubmitting } =
    usePatchAdvisor(advisorId!, config, {
      onSuccess: () => {
        statusNotification("Profile updated successfully", "Success");

        onClose();
      },
      onError: () => {
        statusNotification("Failed to update profile", "Error");
      },
    });

  const methods = useForm<Form>({
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: {
      [Organization]: { label: "Farther" },
    },
  });

  const {
    getValues,
    setValue,
    trigger,
    formState: { isValid, isDirty },
  } = methods;

  // Load advisor profile data on component mount
  useEffect(() => {
    if (currentAdvisor) {
      // Populate form with existing advisor data
      setValue(FirstName, currentAdvisor.name.first);
      setValue(LastName, currentAdvisor.name.last);
      setValue(Hometown, currentAdvisor.details.hometown || "");
      setValue(FartherEmail, currentAdvisor.emailAddress);
      setValue(
        CurrentCity,
        currentAdvisor.details.location?.split(", ")[0] || ""
      );
      setValue(
        CurrentState,
        currentAdvisor.details.location?.split(", ")[1]
          ? {
              label: currentAdvisor.details.location.split(", ")[1] as USAState,
            }
          : undefined
      );
      // Parse almaMaters from API - it comes as a stringified array, display as comma-separated
      let universityValue = "";
      if (currentAdvisor.details.almaMaters) {
        try {
          const parsed = JSON.parse(currentAdvisor.details.almaMaters);
          if (Array.isArray(parsed)) {
            universityValue = parsed.join(", ");
          } else if (typeof parsed === "string") {
            universityValue = parsed;
          }
        } catch {
          // If parsing fails, use the raw string
          universityValue = currentAdvisor.details.almaMaters;
        }
      }
      setValue(University, universityValue);
      setValue(Concentration, currentAdvisor.details.concentration || "");
      setValue(CalendarUrl, currentAdvisor.details.calendarUrl || "");
      setValue(LinkedinProfile, currentAdvisor.details.linkedinUrl || "");
      setValue(Bio, currentAdvisor.details.description || "");

      // Trigger validation after setting all values
      trigger();
    }
  }, [currentAdvisor, setValue, trigger]);

  useEffect(() => {
    if (profileImage === null) {
      setImgError(null);
      return;
    }

    const reader = new FileReader();

    reader.onload = (event) => {
      if (event.target === null || typeof event.target.result !== "string") {
        setProfileImage(null);
        setImgError("Failed to upload this file");
        return;
      }

      const img = new Image();

      img.onload = () => {
        if (img.width !== img.height) {
          setImgError(
            `The image must be square. Yours dimensions are ${img.width}x${img.height}.`
          );
        } else {
          setImgError(null);
        }
      };

      img.onerror = () => {
        setProfileImage(null);
        setImgError("Failed to upload this file. Ensure the format is PNG.");
      };

      img.src = event.target.result;
    };

    reader.readAsDataURL(profileImage);
  }, [profileImage]);

  const buttonDisabled =
    (!isDirty && profileImage === null) ||
    !isValid ||
    isSubmitting ||
    (profileImage !== null && imgError !== null) ||
    (getValues()[Bio]?.length ?? 0) <= BIO_MIN_LENGTH;

  const submit = async () => {
    if (isSubmitting) {
      statusNotification("Form is being submitted", "Error");
      return;
    }

    if (!isDirty && profileImage === null) {
      statusNotification("Nothing to submit", "Error");
      return;
    }

    if (profileImage !== null && imgError !== null) {
      statusNotification("Invalid profile image", "Error");
      return;
    }

    if (!isValid) {
      statusNotification("Some fields are incomplete", "Error");
      return;
    }

    const values = getValues();

    if (!formIsValid(values)) {
      statusNotification("Some fields are incomplete", "Error");
      return;
    }

    if ((values[Bio]?.length ?? 0) <= BIO_MIN_LENGTH) {
      statusNotification("Missing bio", "Error");
      return;
    }

    const patchData = {
      description: values[Bio],
      location: `${values[CurrentCity]}, ${values[CurrentState].label}`,
      hometown: values[Hometown],
      concentration: values[Concentration],
      calendarUrl: values[CalendarUrl],
      linkedinUrl: values[LinkedinProfile],
      almaMaters:
        values[University] && values[University].trim()
          ? values[University].split(",")
              .map((u) => u.trim())
              .filter((u) => u.length > 0)
          : null,
      headshotFile: profileImage || undefined,
    };

    await patchAdvisor(patchData);
  };

  // Show loading state while fetching advisor data
  if (isLoadingAdvisor) {
    return (
      <Drawer isDrawerOpen onClose={onClose}>
        <LogoLoadingStill onTop />
      </Drawer>
    );
  }

  return (
    <Drawer
      isDrawerOpen
      onClose={onClose}
      footer={
        <Stack direction="row" gap="16px">
          <ButtonPrimary
            text="Cancel"
            variant="outlined"
            buttonType="primary"
            onClick={onClose}
          />

          <ButtonPrimary
            disabled={buttonDisabled}
            text="Save Changes"
            variant="contained"
            buttonType="primary"
            onClick={submit}
          />
        </Stack>
      }
    >
      {isSubmitting && <LogoLoadingStill onTop />}
      <FormProvider {...methods}>
        <div className={styles.title}>Edit Profile</div>

        <FlexWrapper>
          <FormTextField
            name={FirstName}
            label={"First Name"}
            required="Must not be empty"
            disabled
          />

          <FormTextField
            name={LastName}
            label={"Last Name"}
            required="Must not be empty"
            disabled
          />

          <FormTextField
            name={FartherEmail}
            label={"Farther Email"}
            required="Must not be empty"
            rules={{
              validate: {
                isValidEmail: (email) => {
                  return !emailValidationRegex.test(email ?? "")
                    ? "Please enter a valid email"
                    : true;
                },
              },
            }}
            disabled
          />

          <FormTextField
            name={Hometown}
            label={"Hometown"}
            required="Must not be empty"
          />

          <FormDropdownField
            name={Organization}
            label={"Organization"}
            values={partners.map((p) => ({ label: p }))}
            required={"Must not be empty"}
            disableSearch
          />

          <FormTextField
            name={CurrentCity}
            label={"Current City"}
            required="Must not be empty"
          />

          <FormDropdownField
            name="CurrentState"
            label="Current State"
            values={USAStates.map((s) => ({ label: s }))}
            required={"Must not be empty"}
            disableSearch
          />

          <FormTextField
            name={University}
            label={"University"}
            placeholder="e.g. Harvard University, MIT"
            required="Must not be empty"
          />

          <FormTextField
            name={Concentration}
            label={"University Degree / Concentration"}
            required="Must not be empty"
          />

          <FormTextField
            name={CalendarUrl}
            label={"Hubspot Meeting Url"}
            required="Must not be empty"
            rules={{
              validate: {
                isValidUrl: (url) => {
                  return (
                    URL_REGEX.test(url) || "Invalid URL. Must be an HTTPS URL."
                  );
                },
              },
            }}
          />

          <FormTextField
            name={LinkedinProfile}
            label={"Linkedin Profile"}
            required="Must not be empty"
            rules={{
              validate: {
                isValidUrl: (url) => {
                  return (
                    URL_REGEX.test(url) || "Invalid URL. Must be an HTTPS URL."
                  );
                },
              },
            }}
          />
        </FlexWrapper>

        <Spacer verticalSpacing="25px" />

        <FormTextareaField
          name={Bio}
          label="Personal Bio"
          required="Need to add a bio"
          placeholder="Add Bio"
          rules={{
            validate: {
              minLength: (value: string) => {
                return (
                  (value?.length ?? 0) > BIO_MIN_LENGTH || "Need to add a bio"
                );
              },
            },
          }}
          minRows={3}
          maxRows={10}
        />

        <Spacer verticalSpacing="25px" />

        <Box>
          <FormFieldLabel
            label={
              <div className={styles.label}>
                <span>Profile Image</span>

                <Tooltip
                  tooltipText={
                    <ul className={styles.list}>
                      <li>Image must be a square</li>

                      <li>Dimensions should *ideally* be 250x250</li>

                      <li>Image format should be PNG (not JPEG)</li>

                      <li>The background should be transparent</li>
                    </ul>
                  }
                  placement={"top"}
                >
                  <InfoOutlinedIcon style={{ width: "17px", height: "17px" }} />
                </Tooltip>
              </div>
            }
          />

          <FileDropUploadComponent
            file={profileImage}
            onChange={setProfileImage}
            acceptedFileExtensions={[".png"]}
            text={"Drop your file here or "}
            pressText={<div className={styles.fileUploadText}>browse</div>}
          />

          <FormHelperText error className={styles.helperText}>
            {imgError}
          </FormHelperText>
        </Box>
      </FormProvider>
    </Drawer>
  );
};

export default EditProfileDrawer;
